<script lang="ts">
	import { onMount } from 'svelte';
	const ZOOM0 = 10;
	const ZOOM1 = 5;
	const ZOOM2 = 1;
	let steps = $state([ZOOM0]);
	let currentStep = $state(steps[0]);
	let dialog: HTMLDialogElement;
	let { details } = $$slots;
	let { zoom1src, zoom1srcset, zoom2src, zoom2srcset, closeLabel } = $props();
	let defaultSlot: HTMLSlotElement;
	let img: HTMLImageElement | null;
	let imgsrc: string | null | undefined, imgsrcset: string | null | undefined;

	onMount(() => {
		img = defaultSlot.assignedElements()[0].querySelector('img');
		imgsrc = img?.getAttribute('src');
		imgsrcset = img?.getAttribute('srcset');
	});

	if (zoom1src && zoom1srcset) {
		steps = [...steps, ZOOM1];
	}
	if (zoom2src && zoom2srcset) {
		steps = [...steps, ZOOM2];
	}

	const preventOverflow = (e: ToggleEvent) => {
		document.documentElement.classList.toggle(
			'overflow-hidden',
			e.newState === 'open',
		);
	};

	$effect(() => {
		if (currentStep === ZOOM0 && imgsrc && imgsrcset) {
			img?.setAttribute('src', imgsrc);
			img?.setAttribute('srcset', imgsrcset);
		}
		if (currentStep === ZOOM1) {
			img?.setAttribute('src', zoom1src);
			img?.setAttribute('srcset', zoom1srcset);
		}
		if (currentStep === ZOOM2) {
			img?.setAttribute('src', zoom2src);
			img?.setAttribute('srcset', zoom2srcset);
		}
	});
</script>

<svelte:options
	customElement={{
		tag: 'product-teaser',
		shadow: 'open',
		props: {
			zoom1src: { reflect: true, attribute: 'zoom1src' },
			zoom1srcset: { reflect: true, attribute: 'zoom1srcset' },
			zoom2src: { reflect: true, attribute: 'zoom2src' },
			zoom2srcset: { reflect: true, attribute: 'zoom2srcset' },
		},
	}}
/>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	class="wrapper"
	class:zoom={currentStep !== ZOOM0}
	onmouseenter={(currentStep = ZOOM1)}
	onmouseleave={(currentStep = ZOOM0)}
>
	<slot name="heading" />

	<svelte:element this={'slot'} bind:this={defaultSlot} />

	<div class="controls">
		{#each steps as step}
			<button
				type="button"
				aria-pressed={currentStep === step}
				onclick={() => (currentStep = step)}
			>
				1:{step}
			</button>
		{/each}

		{#if details}
			<button
				type="button"
				class="details"
				onclick={() => {
					dialog?.showModal();
				}}>Details</button
			>
		{/if}
	</div>
</div>

{#if details}
	<dialog
		bind:this={dialog}
		ontoggle={preventOverflow}
		closedby="any"
		class="navigation overlay"
	>
		<div>
			<button
				type="button"
				class="button"
				data-style="text"
				onclick={() => dialog.close()}
			>
				{closeLabel}
			</button>
		</div>
		<slot name="details" />
	</dialog>
{/if}

<style lang="postcss">
	@import '../../css/blocks/navigation';
	@import '../../css/blocks/overlay';
	@import '../../css/blocks/button';

	.wrapper {
		display: grid;
		grid-template-rows: auto minmax(0, 1fr) auto;
		grid-template-columns: minmax(0, 1fr);
		overflow: clip;
		height: 100%;
		aspect-ratio: 660 / 337;
		color: var(--color-gray-400);
		background-color: var(--color-gray-100);
		transition: color 100ms ease-out;

		&:hover {
			color: var(--color-red);

			[aria-pressed='true'] {
				--button-bg-color: var(--color-red);
			}
		}
	}

	:global(slot[name='heading']::slotted(*)) {
		grid-row: 1;
		padding: var(--gutter);
		grid-column: 1;
		z-index: 1;
	}

	:global(::slotted(picture)) {
		grid-row: 1 / -1;
		pointer-events: none;
		margin: 12%;
		grid-column: 1;
	}
	:global(.zoom ::slotted(picture)) {
		margin: 0;
	}

	.controls {
		display: flex;
		padding: var(--gutter);
		grid-column: 1;
		pointer-events: none;
		grid-row: 3;
		gap: 0.625rem;
		z-index: 1;

		.details {
			margin-left: auto;
		}

		&:hover button {
			--button-bg-color: var(--color-gray-400);
		}

		button {
			appearance: none;
			border-color: transparent;
			border-radius: 3px;
			font-size: 0.625rem;
			color: var(--color-white);
			backdrop-filter: blur(var(--blur, 4px))
				brightness(var(--button-bg-brightness, -100%));
			background-color: oklch(
				from var(--button-bg-color, var(--color-gray-400)) l c h /
					var(--button-bg-opacity, 0.5)
			);
			transition: all 100ms ease-out;
			pointer-events: auto;
			cursor: pointer;

			&:hover {
				--button-bg-color: var(--color-red);
				--button-bg-opacity: 0.5;
			}
		}
		button[aria-pressed='true'] {
			--button-bg-opacity: 1;
			--button-bg-color: var(--color-red);
		}
	}
	.navigation {
		.button {
			@screen md {
				margin: -6px;
			}
		}
	}
</style>
